import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { SashaBreadcrumbService } from "app_sasha/shared/components/breadcrumb/sasha-breadcrumb.service";
import { TitleService } from "app_sasha/shared/services/title/title.service";
import { Yii } from "libraries/interfaces/Yii";
import { IApiSorting, SortKey, TicketingReportDomainService } from "./ticketing-report-domain.service";
import {
    ConvertTicketsAfx2jsonTypeExtension,
    RequestForTicketingReportSearch,
    TicketAirType
} from "@afidium/ts-facade";
import { INotification, NotificationType } from "libraries/interfaces/INotification";
import { ILoader } from "libraries/component/loader/ILoader";
import { ActivatedRoute, Params, Router, UrlSegment } from "@angular/router";
import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";
import { catchError, filter, map, mergeMap, tap } from "rxjs/operators";
import { Observable, of } from "rxjs";
import { RuntimeError } from "libraries/service/error-tracing/runtime-error.model";
import _isEmpty from "lodash-es/isEmpty";
import { QueryParamsMappingService } from "app_sasha/pages/ticketing-report/query-params-mapping.service";
import { IPageChange, IPaginationRecordset } from "libraries/component/pagination/IPagination";
import _cloneDeep from "lodash-es/cloneDeep";
import { ISortOutput, SortDirection } from "libraries/interfaces/ISort";
import { TicketingTableHelperService } from "./components/ticketing-table/ticketing-table-helper.service";
import { SashaPageComponent } from "app_sasha/pages/sasha-page.component";
import { ViewsService } from "libraries/view/views.service";
import { AppRootService } from "app/shared/services/app-root/app-root-service";
import { IApiExportResponse } from "libraries/interfaces/IApiExportResponse";
import { MIN_HMPR_TOTAL } from "app_sasha/pages/ticketing-report/components/ticketing-table/ticketing-table.component";

export interface ITicketingSearchParams {
    isEmptySearch?: boolean;
    free?: string[];
    actTyp?: string[];
    actEveMin?: string;
    actEveMax?: string;
    actAut?: string[];
    bokRef?: string[];
    bokCreMin?: string;
    bokCreMax?: string;
    bokBegMin?: string;
    bokBegMax?: string;
    ticSys?: string[];
    ticTyp?: string[];
    ticVen?: string[];
    ticNum?: string[];
    ticAgc?: string[];
    ticAgt?: string[];
    ticTrav?: string[];
    ticAmtMin?: number;
    ticAmtMax?: number;
    ticComMin?: number;
    ticComMax?: number;
    ticEmiMin?: string;
    ticEmiMax?: string;
    ticPay?: string[];
    bokCFN?: string[];
    bokCFV?: string[];
    sortKey?: SortKey;
    sortDirection?: SortDirection;
    searchTimestamp?: number;
}

class Wording {
    public readonly loading: string = Yii.t('wui-sasha-js', 'Recherche en cours...');
    public readonly exportSuccess: string = Yii.t('wui-sasha-js', "L'export a été correctement effectué : ");
    public readonly exportError: string = Yii.t('wui-sasha-js', 'Erreur interne');
}

@Component({
    selector: 'ticket-search',
    template: `
        <div class="app-wrapper app-sasha">
            <sasha-breadcrumb></sasha-breadcrumb>
            <ticketing-search-form [(isContentVisible)]="isSearchFormVisible"
                                   [searchRequest]="searchRequest"
                                   (onSearch)="navigateToSearch($event, true)"
                                   (onReset)="resetSearch()">
            </ticketing-search-form>

            <ticketing-table [ticketArray]="ticketArray"
                             [recordset]="recordset"
                             [loader]="loader"
                             [notification]="notification"
                             [exportNotification]="exportNotification"
                             [exportLoader]="exportLoader"
                             (onPageChange)="paginate($event)"
                             (onExport)="exportSearchResult()"
                             (onSort)="sort($event)"></ticketing-table>
        </div>
    `
})
export class TicketingReportPageComponent extends SashaPageComponent implements OnInit {

    public readonly wording: Wording = new Wording();

    public isSearchFormVisible: boolean = true;
    public notification: INotification;
    public loader: ILoader;

    public ticketArray: TicketAirType[];
    public searchRequest: RequestForTicketingReportSearch;

    public listLength: number;
    public recordset: IPaginationRecordset = {
        first : 0,
        nbrecord : 50,
    };
    public results: TicketAirType[];

    public storedPageChange?: IPageChange;

    public exportNotification: INotification = {};
    public exportLoader: ILoader = {
        message: Yii.t('wui-sasha-js', 'Export en cours...'),
        active: false,
    };

    public apiSorting: IApiSorting = {
        fieldKey: SortKey.eventDate,
        order: SortDirection.descending,
    };

    constructor(
        protected views: ViewsService,
        readonly appRootService: AppRootService,
        private readonly titleService: TitleService,
        private readonly sashaBreadcrumbService: SashaBreadcrumbService,
        private readonly ticketSearchDomainService: TicketingReportDomainService,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly errorTracingService: ErrorTracingService,
        private readonly queryParamsMappingService: QueryParamsMappingService,
        private readonly changeDetector: ChangeDetectorRef) {
        super(views, appRootService);
    }

    public ngOnInit(): void {
        super.ngOnInit();
        this.titleService.setTitle(Yii.t("wui-sasha-js", "Recherche sur le journal d'émissions"));
        this.sashaBreadcrumbService.setTicketingReportSearch();
        this.listenToQueryParams();
    }

    public resetSearch(): void {
        const targetRoute: UrlSegment[] = this.activatedRoute.snapshot.url;
        this.router.navigate(targetRoute)
            // tslint:disable-next-line:no-any
            .catch((reason: any): void => {
                this.errorTracingService.captureException(new RuntimeError(`Can't navigate to ${targetRoute.join('/')}`, reason));
            });
    }

    public navigateToSearch(formValue: RequestForTicketingReportSearch, newRequest?: boolean): void {
        if(newRequest){
            this.recordset.first = 0;
            delete this.recordset.total;
        }
        this.exportLoader.active = false;
        this.exportNotification.active = false;
        this.results = [];
        this.ticketArray = [];
        this.isSearchFormVisible = false;
        this.listLength = 0;
        this.changeDetector.detectChanges();
        let queryParams: ITicketingSearchParams = {isEmptySearch: true, sortKey: this.apiSorting.fieldKey, sortDirection: this.apiSorting.order, searchTimestamp: new Date().getTime()};
        if (!_isEmpty(formValue)) {
            queryParams = this.queryParamsMappingService.mapSearchToQueryParams(formValue, this.apiSorting);
        }
        const targetRoute: UrlSegment[] = this.activatedRoute.snapshot.url;
        this.router.navigate(targetRoute, {queryParams})
            // tslint:disable-next-line:no-any
            .catch((reason: any): void => {
                this.errorTracingService.captureException(new RuntimeError(`Can't navigate to ${targetRoute.join('/')}`, reason));
            });
    }

    public paginate(pageChange?: IPageChange): void {
        this.recordset = {
            ...this.recordset,
            first : pageChange.first,
            nbrecord : pageChange.rows,
        };

        this.navigateToSearch(this.searchRequest);
    }

    public sort(sortOutput: ISortOutput): void {
        this.apiSorting = {
            fieldKey: sortOutput.actionName as SortKey,
            order: sortOutput.direction,
        };
        if(this.ticketArray?.length){
            this.navigateToSearch(this.searchRequest);
        }
    }

    public exportSearchResult(): void {
        this.exportNotification = {
            active: false,
        };
        this.exportLoader.active = true;

        const request: RequestForTicketingReportSearch = {
            ...this.searchRequest,
            recordset: {
                ...this.recordset,
                total: Math.min(MIN_HMPR_TOTAL, this.recordset.total),
            }
        };

        this.ticketSearchDomainService.export(request)
            .subscribe({
                next: (response: IApiExportResponse): void => {
                    const message: string = this.wording.exportSuccess;
                    const link: string = `<a href="${response.url}">${response.name}</a>`;
                    this.exportLoader.active = false;
                    this.exportNotification = {
                        type: NotificationType.Success,
                        message: message + link,
                        active: true,
                    };
                },
                error: (error: Error): void => {
                    this.exportLoader.active = false;
                    this.exportNotification = {
                        type: NotificationType.Error,
                        message: error.message || this.wording.exportError,
                        active: true,
                    };
                }
            });
    }

    private listenToQueryParams(): void {
        this.activatedRoute.queryParams.pipe(
            tap((params: Params): void => {
                if (_isEmpty(params)) {
                    this.resetState();
                }
            }),
            filter((params: Params): boolean => !_isEmpty(params)),
            tap((): void => {
                this.ticketArray = null;
                this.loader = {
                    message: this.wording.loading,
                    active: true,
                };
                this.notification = null;
                this.isSearchFormVisible = false;
                this.changeDetector.markForCheck();
            }),
            map((params: Params): RequestForTicketingReportSearch => this.queryParamsMappingService.mapQueryParamsToSearch(params)),
            mergeMap((request: RequestForTicketingReportSearch): Observable<ConvertTicketsAfx2jsonTypeExtension> => {
                this.searchRequest = request;
                //@ts-ignore
                //TODO JSV : only to be able to compile in jenkins
                //while finding a real solution
                return this.ticketSearchDomainService.search({...request, recordset: this.recordset})
                    .pipe(catchError((err: string): Observable<ConvertTicketsAfx2jsonTypeExtension> => {
                        this.notification = {
                            message: err,
                            type: NotificationType.Error,
                            active: true,
                        };
                        this.errorTracingService.captureException(err);
                        return of(null);
                    }));
            }),
            tap((response: ConvertTicketsAfx2jsonTypeExtension): void => {
                if (response.ticketArray?.length) {
                    this.isSearchFormVisible = false;
                }
                this.ticketArray = response.ticketArray;
                this.recordset = response.recordset as IPaginationRecordset;
                this.loader = null;
            }),
        ).subscribe();
    }

    private resetState(): void {
        this.searchRequest = null;
        this.isSearchFormVisible = true;
        this.loader = null;
        this.notification = null;
        this.ticketArray = null;
    }
}
